/**
 * AccountSteps.tsx — the compact "two steps to your account" indicator on the
 * checkout account step.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: journey-ux L5 (post-payment
 * celebration, CTO 2026-09-24), cut to TWO steps by the CTO's second pass the
 * same day.
 *
 * WHY: straight after paying, a guest meets a form, and then — after pressing
 * "Create account" — a second screen asking for an emailed code they were not
 * told was coming. Naming the steps up front turns the code from a surprise into
 * the step they expected.
 *
 * TWO STEPS, NOT THREE (CTO 2026-09-24): *"I don't think we need to show the
 * third step there because, as soon as they enter the code and they click
 * Continue, it goes to the next step."* "Go to your account" was not something
 * the patient does, so it is gone. The labels live in `accountStepCopy.ts`.
 *
 * ONLY WHERE IT IS TRUE, IN THE ORDER IT IS TRUE. `lane` picks the order:
 * form-first is password → code; code-first (a cold return) is code → password.
 * A patient who proved their email upstream is never asked for a code at all, so
 * `CheckoutFlow` renders no indicator for them, and none on the sign-in view a
 * returning patient is switched to. A wrong indicator is worse than none.
 *
 * STATE IS NEVER COLOUR ALONE (WCAG 1.4.1): the current step is bold with a
 * filled numbered mark and `aria-current="step"`; a finished step swaps its
 * number for a tick and says "done" to a screen reader; an upcoming one keeps a
 * plain outlined number.
 *
 * A SHORT NUMBERED LIST, NOT A SECOND STEPPER. `CheckoutProgress` already sits
 * at the top of this screen as a horizontal stepper (plan → delivery & payment
 * → account); a second horizontal bar under it would read as the same thing
 * twice.
 */
import { CheckIcon } from '../ui/kit';
import { ACCOUNT_STEP_LABELS, type AccountLane } from './accountStepCopy';

/**
 * 1-based: which of the two steps the patient is on now; `'done'` once both are
 * behind them (a code verified in this flow, the account being created).
 */
export type AccountStepState = 1 | 2 | 'done';

export function AccountSteps({ lane, current }: { lane: AccountLane; current: AccountStepState }) {
  const labels = ACCOUNT_STEP_LABELS[lane];
  const position = current === 'done' ? labels.length + 1 : current;
  return (
    <ol className="gps-jux-account-steps" aria-label="Setting up your account">
      {labels.map((label, i) => {
        const n = i + 1;
        const state = n < position ? 'done' : n === position ? 'current' : 'upcoming';
        return (
          <li
            key={label}
            className="gps-jux-account-step"
            data-state={state}
            aria-current={state === 'current' ? 'step' : undefined}
          >
            <span className="gps-jux-account-step-mark" aria-hidden="true">
              {state === 'done' ? <CheckIcon size={12} /> : n}
            </span>
            <span className="gps-jux-account-step-label">
              {label}
              {state === 'done' ? <span className="gps-sr-only"> (done)</span> : null}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
